import { supabase } from "@/lib/supabase-client";

export interface ProfileData {
  first_name: string;
  last_name: string;
  preferred_name: string;
  meta_data?: Record<string, any>;
}

export interface ProfileMetaData {
  state?: string;
  city?: string;
  timezone?: string;
  format?: string;
  email_request?: string;
  [key: string]: any;
}

class ProfileService {
  async getProfile(userId: string) {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, preferred_name, meta_data")
      .eq("id", userId)
      .single();
    if (error) {
      throw new Error(`Failed to fetch profile: ${error.message}`);
    }

    return data;
  }

  async updateProfile(userId: string, profileData: Partial<ProfileData>) {
    const { error } = await supabase.from("profiles").update(profileData).eq("id", userId);

    if (error) {
      throw new Error(`Failed to update profile: ${error.message}`);
    }

    return true;
  }

  async updateProfileMetaData(userId: string, metaDataUpdates: ProfileMetaData) {
    const currentProfile = await this.getProfile(userId);
    const currentMetaData = currentProfile.meta_data || {};

    const mergedMetaData = {
      ...currentMetaData,
      ...metaDataUpdates,
    };

    const { error } = await supabase
      .from("profiles")
      .update({
        meta_data: mergedMetaData,
      })
      .eq("id", userId);

    if (error) {
      throw new Error(`Failed to update profile meta_data: ${error.message}`);
    }

    return true;
  }

  async updateFullProfile(
    userId: string,
    updates: {
      profile?: Omit<Partial<ProfileData>, "meta_data">;
      metaData?: ProfileMetaData;
    },
  ) {
    const currentProfile = await this.getProfile(userId);
    const currentMetaData = currentProfile.meta_data || {};

    const profileUpdate: Partial<ProfileData> = {
      ...updates.profile,
    };

    if (updates.metaData) {
      profileUpdate.meta_data = {
        ...currentMetaData,
        ...updates.metaData,
      };
    }

    const { error } = await supabase.from("profiles").update(profileUpdate).eq("id", userId);

    if (error) {
      throw new Error(`Failed to update profile: ${error.message}`);
    }

    return true;
  }

  async getProfilesByIds(userIds: string[]) {
    if (userIds.length === 0) {
      return [];
    }

    const { data: profiles, error } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, preferred_name")
      .in("id", userIds);

    if (error) {
      throw new Error(`Failed to fetch profiles: ${error.message}`);
    }

    return profiles || [];
  }
}

export const profileService = new ProfileService();
